
import ExcelJS from 'exceljs';
import {
  transformSuccessCriteriaToCSV,
  transformSummaryToCSV,
  transformRubricsToCSV,
  transformShortAnswerToCSV,
} from './dataTransformers';
import { SECTIONS } from './fileNaming';

const COLORS = {
  purple: '7C3AED',
  green: '22C55E',
  yellow: 'EAB308',
  red: 'EF4444',
  blue: '3B82F6',
  gray: '6B7280',
};

const PIE_CHART_COLORS = [
  '#1ade54', '#9f6bfa', '#fa6be7', '#6bfaee', '#faee6b',
  '#406bda', '#f1955c', '#ff6b9d', '#00bcd4', '#8bc34a'
];

function generatePieChartColor(index) {
  return PIE_CHART_COLORS[index % PIE_CHART_COLORS.length];
}

function hexToArgb(hex) {
  const cleanHex = hex.replace('#', '');
  return 'FF' + cleanHex.toUpperCase();
}

function buildDetailedCommentBankRows(commentBankStats) {
  if (!Array.isArray(commentBankStats) || commentBankStats.length === 0) {
    return [];
  }

  const firstItem = commentBankStats[0];
  const isPreformatted = firstItem && firstItem['Feedback Area'] !== undefined;

  if (isPreformatted) {
    const feedbackColorMap = new Map();
    let suggestionColorIndex = 0;
    const getFeedbackColor = (feedbackName) => {
      const key = String(feedbackName || '').trim().toLowerCase();
      if (!key) return null;
      if (!feedbackColorMap.has(key)) {
        feedbackColorMap.set(key, generatePieChartColor(suggestionColorIndex));
        suggestionColorIndex += 1;
      }
      return feedbackColorMap.get(key);
    };
    return commentBankStats.map((item) => {
      const hasDetailedColumns =
        item['Feedback Area %'] !== undefined ||
        item.Suggestion !== undefined ||
        item['Suggestion %'] !== undefined;
      const name = String(item['Feedback Area'] || '').trim();
      const normalizedName = name.trim();
      const percentageRaw = String(
        hasDetailedColumns ? item['Feedback Area %'] : item['% Frequency'] || ''
      )
        .replace('%', '')
        .trim();
      const suggestionPercentageRaw = String(item['Suggestion %'] || '')
        .replace('%', '')
        .trim();
      const hasPercentage = percentageRaw !== '' && !Number.isNaN(Number(percentageRaw));
      const percentage = hasPercentage ? Number(Number(percentageRaw).toFixed(1)) : null;
      const hasSuggestionPercentage =
        suggestionPercentageRaw !== '' && !Number.isNaN(Number(suggestionPercentageRaw));
      const suggestionPercentage = hasSuggestionPercentage
        ? Number(Number(suggestionPercentageRaw).toFixed(1))
        : null;

      let type = 'suggestion';
      if (normalizedName.startsWith('Comment Bank:')) {
        type = 'bank';
      } else if (
        normalizedName.startsWith('-') ||
        normalizedName.startsWith('•') ||
        name.startsWith('  -') ||
        (normalizedName === '' && String(item.Suggestion || '').trim() !== '')
      ) {
        type = 'comment';
      } else if (
        normalizedName.startsWith('Suggestion:') ||
        hasDetailedColumns ||
        String(item.Suggestion || '').trim() !== ''
      ) {
        type = 'suggestion';
      }

      const row = {
        name,
        percentage,
        suggestion: hasDetailedColumns ? String(item.Suggestion || '') : '',
        suggestionPercentage,
        count: hasDetailedColumns ? '' : item['Comment Count'] ?? '',
        type,
      };

      if (type === 'suggestion') {
        row.color = getFeedbackColor(name);
      } else if (type === 'comment') {
        row.color = getFeedbackColor(name);
      }

      return row;
    });
  }

  const includeBankSeparator = commentBankStats.length > 1;
  const feedbackColorMap = new Map();
  let suggestionColorIndex = 0;
  const getFeedbackColor = (feedbackName) => {
    const key = String(feedbackName || '').trim().toLowerCase();
    if (!key) return null;
    if (!feedbackColorMap.has(key)) {
      feedbackColorMap.set(key, generatePieChartColor(suggestionColorIndex));
      suggestionColorIndex += 1;
    }
    return feedbackColorMap.get(key);
  };
  const rows = [];

  commentBankStats.forEach((bank, bankIndex) => {
    const bankTitle = String(bank?.title || bank?.name || `Comment Bank ${bankIndex + 1}`).trim();

    if (includeBankSeparator) {
      rows.push({
        name: `Comment Bank: ${bankTitle}`,
        percentage: null,
        count: '',
        type: 'bank',
      });
    }

    (bank?.suggestions || []).forEach((suggestion) => {
      const suggestionTitle = String(suggestion?.title || '').trim();
      if (!suggestionTitle) return;

      const suggestionComments = Array.isArray(suggestion?.comments) ? suggestion.comments : [];
      const suggestionColor = getFeedbackColor(suggestionTitle);

      if (suggestionComments.length === 0) {
        rows.push({
          name: suggestionTitle,
          percentage: Number(suggestion?.usagePercentage || 0),
          suggestion: '-',
          suggestionPercentage: null,
          count: '',
          type: 'suggestion',
          color: suggestionColor,
        });
      }

      suggestionComments.forEach((comment, commentIndex) => {
        const commentText = String(comment?.comment || '').trim();
        if (!commentText) return;
        rows.push({
          name: commentIndex === 0 ? suggestionTitle : '',
          percentage: commentIndex === 0 ? Number(suggestion?.usagePercentage || 0) : null,
          suggestion: commentText,
          suggestionPercentage: Number(comment?.usagePercentage || 0),
          count: '',
          type: 'comment',
          color: suggestionColor,
        });
      });
    });
  });

  return rows;
}

function canvasToBase64(canvas) {
  if (!canvas) {
    throw new Error('Canvas is required for chart export');
  }

  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.split(',')[1];
}

function interpolateGradientColor(percent) {
  const clamped = Math.max(0, Math.min(100, Number(percent) || 0));
  const red = clamped < 50 ? 245 : Math.round(245 - ((clamped - 50) * 145) / 50);
  const green = clamped < 50 ? Math.round(69 + (clamped * 121) / 50) : Math.round(190 + ((clamped - 50) * 49) / 50);
  const blue = clamped < 50 ? 69 : Math.round(69 - ((clamped - 50) * 33) / 50);
  const toHex = (value) => value.toString(16).padStart(2, '0').toUpperCase();
  return `FF${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

function getGradeLevelColor(level) {
  const normalized = String(level || '').trim().toUpperCase();

  // A or Very High or 17-20 -> Green (matches ci-rubric-bar-high)
  if (normalized === 'A' || normalized === '17-20' || normalized === 'VERYHIGH') {
    return '#1BA80F';
  }
  // B or High or 13-16 -> Light Green (still high performance)
  if (normalized === 'B' || normalized === '13-16' || normalized === 'HIGH') {
    return '#1BA80F';
  }
  // C or Middle or 9-12 -> Orange/Yellow (matches ci-rubric-bar-middle)
  if (normalized === 'C' || normalized === '9-12' || normalized === 'MIDDLE') {
    return '#BF8609';
  }
  // D or Low or 5-8 -> Red (matches ci-rubric-bar-low)
  if (normalized === 'D' || normalized === '5-8' || normalized === 'LOW') {
    return '#F03636';
  }
  // E or Very Low or 0-4 -> Red (matches ci-rubric-bar-low)
  if (normalized === 'E' || normalized === '0-4' || normalized === 'VERYLOW') {
    return '#F03636';
  }

  // Handle numeric grade levels (1=highest, 5=lowest typically)
  const numGrade = parseInt(normalized, 10);
  if (!isNaN(numGrade)) {
    if (numGrade === 1) return '#1BA80F'; // Grade 1 = Green (highest)
    if (numGrade === 2) return '#22C55E'; // Grade 2 = Light Green
    if (numGrade === 3) return '#BF8609'; // Grade 3 = Orange/Yellow (middle)
    if (numGrade === 4) return '#F03636'; // Grade 4 = Red
    if (numGrade === 5) return '#DC2626'; // Grade 5 = Dark Red (lowest)
  }

  // Default fallback - use a neutral blue color for unknown grades
  return '#6B7280';
}

function sortGradeLevels(labels) {
  const order = {
    'A': 1, '17-20': 1, 'VERYHIGH': 1,
    'B': 2, '13-16': 2, 'HIGH': 2,
    'C': 3, '9-12': 3, 'MIDDLE': 3,
    'D': 4, '5-8': 4, 'LOW': 4,
    'E': 5, '0-4': 5, 'VERYLOW': 5,
  };

  return [...labels].sort((a, b) => {
    const aKey = String(a || '').trim().toUpperCase();
    const bKey = String(b || '').trim().toUpperCase();

    // Check if both are numeric grades
    const aNum = parseInt(aKey, 10);
    const bNum = parseInt(bKey, 10);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum; // Sort numeric grades ascending (1, 2, 3, 4, 5)
    }

    // Check if one is numeric - numeric grades come first
    if (!isNaN(aNum)) return -1;
    if (!isNaN(bNum)) return 1;

    // Use predefined order for letter grades
    const aOrder = order[aKey] || 999;
    const bOrder = order[bKey] || 999;
    return aOrder - bOrder;
  });
}

function createSuccessCriteriaStackedBarCanvas(csvData) {
  const rows = csvData.slice(1); // Skip header
  if (rows.length === 0) return null;

  console.log('📊 [Excel] Creating success criteria chart with', rows.length, 'items');

  // Show ALL rows - no limit
  const displayRows = rows;

  const canvas = document.createElement('canvas');
  const barHeight = 80; // EXTRA large bars
  const chartTop = 160;
  const chartLeft = 800; // Extra space for long labels
  const chartWidth = 1500; // Extra wide bars
  const canvasHeight = Math.max(chartTop + (displayRows.length * (barHeight + 15)) + 120, 1200);

  canvas.width = 3000; // EXTRA wide canvas
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // White background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Title
  ctx.font = 'bold 42px Arial';
  ctx.fillStyle = '#4B464F';
  ctx.textAlign = 'left';
  ctx.fillText('Success Criteria Distribution', 70, 75);

  // Legend
  const legendY = 115;
  const legendItems = [
    { label: 'Satisfied', color: '#22C55E' },
    { label: 'Partially Satisfied', color: '#EAB308' },
    { label: 'Not Satisfied', color: '#EF4444' },
  ];

  let legendX = 70;
  ctx.font = '24px Arial';
  legendItems.forEach((item) => {
    ctx.fillStyle = item.color;
    ctx.fillRect(legendX, legendY, 30, 30);
    ctx.fillStyle = '#3A3540';
    ctx.textAlign = 'left';
    ctx.fillText(item.label, legendX + 40, legendY + 23);
    legendX += ctx.measureText(item.label).width + 90;
  });

  // Draw stacked bars
  displayRows.forEach((row, index) => {
    const y = chartTop + (index * (barHeight + 15));
    const criteriaLabel = String(row[0] || '').substring(0, 60);
    const objectiveLabel = String(row[1] || '').substring(0, 60);

    // Label
    ctx.font = '20px Arial';
    ctx.fillStyle = '#3A3540';
    ctx.textAlign = 'right';
    const labelText = `${criteriaLabel} - ${objectiveLabel}`;
    ctx.fillText(labelText, chartLeft - 30, y + (barHeight / 2) + 7);

    // Parse percentages
    const satisfied = parseFloat(String(row[2] || '0').replace('%', '')) || 0;
    const partial = parseFloat(String(row[3] || '0').replace('%', '')) || 0;
    const notSatisfied = parseFloat(String(row[4] || '0').replace('%', '')) || 0;
    const total = satisfied + partial + notSatisfied;

    if (total === 0) return;

    // Draw stacked bar segments
    let currentX = chartLeft;

    // Satisfied (green)
    if (satisfied > 0) {
      const width = (satisfied / 100) * chartWidth;
      ctx.fillStyle = '#22C55E';
      ctx.fillRect(currentX, y, width, barHeight);

      // Show percentage if segment is large enough
      if (satisfied > 5) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${satisfied.toFixed(1)}%`, currentX + width / 2, y + (barHeight / 2) + 8);
      }
      currentX += width;
    }

    // Partially Satisfied (yellow)
    if (partial > 0) {
      const width = (partial / 100) * chartWidth;
      ctx.fillStyle = '#EAB308';
      ctx.fillRect(currentX, y, width, barHeight);

      if (partial > 5) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${partial.toFixed(1)}%`, currentX + width / 2, y + (barHeight / 2) + 8);
      }
      currentX += width;
    }

    // Not Satisfied (red)
    if (notSatisfied > 0) {
      const width = (notSatisfied / 100) * chartWidth;
      ctx.fillStyle = '#EF4444';
      ctx.fillRect(currentX, y, width, barHeight);

      if (notSatisfied > 5) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${notSatisfied.toFixed(1)}%`, currentX + width / 2, y + (barHeight / 2) + 8);
      }
    }
  });

  return canvas;
}

function createRubricsChartCanvas(csvData) {
  const rows = csvData.slice(1);
  const counts = new Map();

  console.log('📊 [Excel] Creating rubrics chart from', rows.length, 'rows');

  rows.forEach((row, idx) => {
    const levelValue = row?.[3] || '';
    const levels = String(levelValue)
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean);

    if (idx < 3) {
      console.log('📊 [Excel] Sample rubrics row', idx, ':', {
        rawValue: levelValue,
        parsedLevels: levels
      });
    }

    // Skip only if completely empty or explicitly marked as no data
    if (levels.length === 0 || levels[0] === '-') return;

    levels.forEach((level) => {
      counts.set(level, (counts.get(level) || 0) + 1);
    });
  });

  console.log('📊 [Excel] Rubrics grade distribution:', Object.fromEntries(counts));

  if (counts.size === 0) {
    console.warn('⚠️ [Excel] No rubrics data to chart');
    return null;
  }

  const unsortedLabels = Array.from(counts.keys());
  const labels = sortGradeLevels(unsortedLabels);
  const values = labels.map((label) => counts.get(label));
  const maxVal = Math.max(...values, 1);

  console.log('📊 [Excel] Rubrics chart will display', labels.length, 'grade levels:', labels);

  const canvas = document.createElement('canvas');
  canvas.width = 1600; // Much wider for more grade levels
  canvas.height = 700; // Taller for better visibility
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.font = 'bold 28px Arial';
  ctx.fillStyle = '#4B464F';
  ctx.fillText('Rubrics Grade Distribution', 50, 60);

  const chartLeft = 100;
  const chartBottom = 600;
  const chartHeight = 450;
  const availableWidth = canvas.width - chartLeft - 80;
  const barGap = 35;
  const barWidth = Math.max(40, Math.floor((availableWidth - (labels.length - 1) * barGap) / labels.length));

  ctx.strokeStyle = '#D9D3DE';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(chartLeft, chartBottom - chartHeight);
  ctx.lineTo(chartLeft, chartBottom);
  ctx.lineTo(canvas.width - 40, chartBottom);
  ctx.stroke();

  labels.forEach((label, index) => {
    const value = values[index];
    const barHeight = Math.round((value / maxVal) * (chartHeight - 20));
    const x = chartLeft + index * (barWidth + barGap);
    const y = chartBottom - barHeight;

    ctx.fillStyle = getGradeLevelColor(label);
    ctx.fillRect(x, y, barWidth, barHeight);

    ctx.fillStyle = '#3A3540';
    ctx.font = '18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(String(label), x + barWidth / 2, chartBottom + 30);

    // Value on top of bar
    ctx.font = 'bold 20px Arial';
    ctx.fillText(String(value), x + barWidth / 2, y - 12);
  });

  console.log('✅ [Excel] Rubrics chart created with', labels.length, 'bars');

  return canvas;
}

function createCommentBanksPieCanvas(commentBankStats) {
  console.log('📊 [Excel] createCommentBanksPieCanvas called with:', {
    hasData: !!commentBankStats,
    isArray: Array.isArray(commentBankStats),
    length: commentBankStats?.length,
  });

  const allRows = buildDetailedCommentBankRows(commentBankStats);

  // Filter for suggestion rows OR comment rows that have a percentage (first comment of each suggestion)
  // This handles both cases: suggestions without comments (type='suggestion') and suggestions with comments (type='comment' with percentage)
  const rows = allRows.filter(
    (item) => (item.type === 'suggestion' || item.type === 'comment') &&
              Number(item.percentage) > 0 &&
              item.name &&
              item.name.trim() !== ''
  );

  // Group by name to ensure we only show each feedback area once in the pie chart
  const uniqueRows = [];
  const seenNames = new Set();
  rows.forEach((row) => {
    const normalizedName = String(row.name || '').trim().toLowerCase();
    if (normalizedName && !seenNames.has(normalizedName)) {
      seenNames.add(normalizedName);
      uniqueRows.push(row);
    }
  });

  console.log('📊 [Excel] Pie chart data processed:', {
    totalRows: allRows.length,
    filteredRows: rows.length,
    uniqueRows: uniqueRows.length,
    rowTypes: allRows.reduce((acc, r) => { acc[r.type] = (acc[r.type] || 0) + 1; return acc; }, {}),
    sampleData: uniqueRows.slice(0, 3).map(r => ({
      name: r.name,
      percentage: r.percentage,
      type: r.type,
      color: r.color
    }))
  });

  if (uniqueRows.length === 0) {
    console.warn('⚠️ [Excel] No valid rows with percentage > 0 for pie chart. All rows:',
      allRows.slice(0, 5).map(r => ({ name: r.name, type: r.type, percentage: r.percentage }))
    );
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1600;
  canvas.height = Math.max(900, 140 + uniqueRows.length * 36 + 80);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('❌ [Excel] Failed to get canvas context');
    return null;
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const cx = 400;
  const cy = 450;
  const radius = 320;
  const total = uniqueRows.reduce((sum, row) => sum + Number(row.percentage || 0), 0);
  if (total <= 0) {
    console.warn('⚠️ [Excel] Total percentage is 0. Rows:', uniqueRows.map(r => ({ name: r.name, percentage: r.percentage })));
    return null;
  }

  console.log('✅ [Excel] Generating pie chart with', uniqueRows.length, 'slices, total:', total.toFixed(1), '%');

  let startAngle = -Math.PI / 2;
  uniqueRows.forEach((row, index) => {
    const value = Number(row.percentage || 0);
    const slice = (value / total) * Math.PI * 2;
    const endAngle = startAngle + slice;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = row.color || generatePieChartColor(index);
    ctx.fill();
    startAngle = endAngle;
  });

  // Title
  ctx.font = 'bold 32px Arial';
  ctx.fillStyle = '#4B464F';
  ctx.textAlign = 'left';
  ctx.fillText('Feedback Area Distribution', 60, 70);

  // Legend
  ctx.font = '18px Arial';
  const legendX = 850;
  let legendY = 140;
  uniqueRows.forEach((row, index) => {
    const color = row.color || generatePieChartColor(index);
    ctx.fillStyle = color;
    ctx.fillRect(legendX, legendY, 24, 24);
    ctx.fillStyle = '#3A3540';
    const label = `${row.name} (${Number(row.percentage).toFixed(1)}%)`;
    ctx.fillText(label, legendX + 34, legendY + 18);
    legendY += 36;
  });

  console.log('✅ [Excel] Pie chart canvas generated successfully with', uniqueRows.length, 'slices');
  return canvas;
}

function drawDonutArc(ctx, cx, cy, innerR, outerR, startAngle, endAngle, color) {
  if (startAngle === endAngle) return;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR, startAngle, endAngle, false);
  ctx.arc(cx, cy, innerR, endAngle, startAngle, true);
  ctx.closePath();
  ctx.fill();
}

function createSummaryDonutCanvas(submissionsStats) {
  const classwork = Number(submissionsStats?.classwork) || 0;
  const independent = Number(submissionsStats?.independent) || 0;
  const total = Number(submissionsStats?.total) || classwork + independent;
  const sum = classwork + independent;

  const canvas = document.createElement('canvas');
  canvas.width = 900;
  canvas.height = 520;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const cx = canvas.width / 2;
  const cy = 340;
  const outerR = 160;
  const innerR = 98;
  const classworkShare = sum > 0 ? classwork / sum : 0.5;
  const startAngle = Math.PI;
  const endAngle = 0;
  const splitAngle = Math.PI - classworkShare * Math.PI;

  ctx.font = 'bold 26px Arial';
  ctx.fillStyle = '#4B464F';
  ctx.textAlign = 'center';
  ctx.fillText('Submission Type Distribution', cx, 48);

  if (sum === 0) {
    drawDonutArc(ctx, cx, cy, innerR, outerR, startAngle, endAngle, '#E5E2E8');
  } else {
    if (classworkShare < 1) {
      drawDonutArc(ctx, cx, cy, innerR, outerR, splitAngle, endAngle, '#1ADE54');
    }
    if (classworkShare > 0) {
      drawDonutArc(ctx, cx, cy, innerR, outerR, startAngle, splitAngle, '#6E4BDE');
    }
  }

  ctx.font = 'bold 52px Arial';
  ctx.fillStyle = '#4B464F';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(total), cx, cy + 8);

  const classworkPercent = sum > 0 ? ((classwork / sum) * 100).toFixed(1) : '0.0';
  const independentPercent = sum > 0 ? ((independent / sum) * 100).toFixed(1) : '0.0';

  const legendY = 430;
  ctx.font = 'bold 20px Arial';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#6E4BDE';
  ctx.fillRect(120, legendY, 24, 24);
  ctx.fillStyle = '#4B464F';
  ctx.fillText(`Classwork: ${classwork} (${classworkPercent}%)`, 154, legendY + 18);

  ctx.fillStyle = '#1ADE54';
  ctx.fillRect(120, legendY + 40, 24, 24);
  ctx.fillStyle = '#4B464F';
  ctx.fillText(`Independent Study: ${independent} (${independentPercent}%)`, 154, legendY + 58);

  console.log('✅ [Excel] Summary donut generated programmatically:', { total, classwork, independent });
  return canvas;
}

async function addCommentBanksSheet(worksheet, workbook, commentBankStats) {
  console.log('📊 [Excel] Adding Comment Banks sheet with data:', {
    hasData: !!commentBankStats,
    isArray: Array.isArray(commentBankStats),
    length: commentBankStats?.length,
    firstItem: commentBankStats?.[0]
  });

  const dataRows = buildDetailedCommentBankRows(commentBankStats);

  console.log('📊 [Excel] Comment Banks data rows:', {
    totalRows: dataRows.length,
    suggestionRows: dataRows.filter(r => r.type === 'suggestion').length,
    commentRows: dataRows.filter(r => r.type === 'comment').length,
    sampleRows: dataRows.slice(0, 3)
  });


  worksheet.mergeCells('A1:E1');
  worksheet.getCell('A1').value = 'Comment Banks Analysis';
  worksheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF' + COLORS.purple } };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.mergeCells('A2:E2');
  const descCell = worksheet.getCell('A2');
  descCell.value = 'This sheet shows feedback areas and their suggestions. Color-coded percentages match the pie chart below.';
  descCell.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } };
  descCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

  worksheet.getRow(4).values = ['Type', 'Feedback Area', 'Frequency %', 'Suggestion Text', 'Suggestion %'];
  worksheet.getRow(4).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(4).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF' + COLORS.purple },
  };
  worksheet.getRow(4).alignment = { horizontal: 'center', vertical: 'middle' };

  let currentRow = 5;

  if (dataRows.length === 0) {
    worksheet.getRow(currentRow).values = ['', 'No data available', '', '', ''];
    currentRow++;
  } else {
    dataRows.forEach((item) => {
      const row = worksheet.getRow(currentRow);
      const hasPercentage = item.percentage !== null && item.percentage !== undefined;
      const hasSuggestionPercentage =
        item.suggestionPercentage !== null && item.suggestionPercentage !== undefined;

      let typeLabel = '';
      if (item.type === 'bank') {
        typeLabel = 'Bank';
      } else if (item.type === 'suggestion') {
        typeLabel = 'Feedback';
      } else if (item.type === 'comment') {
        typeLabel = '  └ Detail';
      }

      row.values = [
        typeLabel,
        item.name,
        hasPercentage ? `${Number(item.percentage).toFixed(1)}%` : '',
        item.suggestion || '',
        hasSuggestionPercentage ? `${Number(item.suggestionPercentage).toFixed(1)}%` : '',
      ];

      if (item.type === 'bank') {
        row.getCell(1).font = { size: 11, bold: true, color: { argb: 'FF' + COLORS.purple } };
        row.getCell(2).font = { size: 11, bold: true, color: { argb: 'FF' + COLORS.purple } };
        row.getCell(3).value = '';
        row.getCell(4).value = '';
        row.getCell(5).value = '';
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF3F4F6' }, // Light gray background for bank rows
        };
      } else if (item.type === 'comment') {
        row.getCell(1).font = { size: 10, color: { argb: 'FF6B7280' } };
        row.getCell(2).font = { size: 10, color: { argb: 'FF4B5563' } };
        row.getCell(3).font = { size: 10, color: { argb: 'FF374151' } };
        row.getCell(4).font = { size: 10, color: { argb: 'FF374151' } };
        row.getCell(5).font = { size: 10, bold: true, color: { argb: 'FF374151' } };
      } else {
        row.getCell(1).font = { size: 11, bold: true, color: { argb: 'FF' + COLORS.purple } };
        row.getCell(2).font = { size: 11, bold: true };
        row.getCell(3).font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
        row.getCell(4).font = { size: 11 };
        row.getCell(5).font = { size: 11, bold: true, color: { argb: 'FF374151' } };
      }

      if (hasPercentage && item.type === 'suggestion') {
        const baseColor = item.color || '#6B7280';
        const argbColor = hexToArgb(baseColor);
        row.getCell(3).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: argbColor },
        };
      }

      row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
      row.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(4).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      row.getCell(5).alignment = { horizontal: 'center', vertical: 'middle' };

      currentRow++;
    });
  }

  worksheet.getColumn(1).width = 12; // Type
  worksheet.getColumn(2).width = 50; // Feedback Area
  worksheet.getColumn(3).width = 18; // Frequency %
  worksheet.getColumn(4).width = 78; // Suggestion Text
  worksheet.getColumn(5).width = 16; // Suggestion %

  const chartStartRow = currentRow + 2;

  try {
    console.log('📊 [Excel] Attempting to create pie chart for Comment Banks...');
    const pieCanvas = createCommentBanksPieCanvas(commentBankStats);

    if (pieCanvas) {
      console.log('📊 [Excel] Converting pie chart canvas to base64...');
      const base64Image = canvasToBase64(pieCanvas);

      console.log('📊 [Excel] Adding pie chart image to workbook...');
      const imageId = workbook.addImage({
        base64: base64Image,
        extension: 'png',
      });

      worksheet.mergeCells(`A${chartStartRow}:E${chartStartRow}`);
      const chartTitleCell = worksheet.getCell(`A${chartStartRow}`);
      chartTitleCell.value = 'Visual Chart: Feedback Area Distribution';
      chartTitleCell.font = { bold: true, size: 12, color: { argb: 'FF' + COLORS.purple } };
      chartTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      chartTitleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' },
      };

      worksheet.mergeCells(`A${chartStartRow + 1}:E${chartStartRow + 1}`);
      const chartDescCell = worksheet.getCell(`A${chartStartRow + 1}`);
      chartDescCell.value = 'The colors in the chart match the "Frequency %" column in the data table above. Each slice represents a feedback area.';
      chartDescCell.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } };
      chartDescCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

      console.log('📊 [Excel] Adding pie chart to worksheet at row', chartStartRow + 2);
      worksheet.addImage(imageId, {
        tl: { col: 0, row: chartStartRow + 2 },
        ext: { width: 1200, height: 750 }, // Much larger chart
        editAs: 'oneCell'
      });

      console.log('✅ [Excel] Pie chart successfully added to Comment Banks sheet!');
    } else {
      console.warn('⚠️ [Excel] Pie chart canvas could not be created (null returned). Check data format and percentages.');
      worksheet.mergeCells(`A${chartStartRow}:E${chartStartRow}`);
      const chartCell = worksheet.getCell(`A${chartStartRow}`);
      chartCell.value = 'Note: The pie chart could not be exported (no data with valid percentages). The color-coded percentages in the "Frequency %" column represent the visual distribution.';
      chartCell.font = { italic: true, size: 10, color: { argb: 'FFEF4444' } };
      chartCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      chartCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFEF2F2' },
      };
    }
  } catch (error) {
    console.error('❌ [Excel] Exception while adding chart to Comment Banks sheet:', error);
    console.error('❌ [Excel] Error stack:', error.stack);
    worksheet.mergeCells(`A${chartStartRow}:E${chartStartRow}`);
    const errorCell = worksheet.getCell(`A${chartStartRow}`);
    errorCell.value = `Error: Failed to export chart: ${error.message || 'Unknown error'}. The color-coded percentages in the "Frequency %" column represent the visual distribution.`;
    errorCell.font = { italic: true, size: 10, color: { argb: 'FFEF4444' } };
    errorCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  }
}

async function addSuccessCriteriaSheet(worksheet, workbook, successCriteriaStats) {
  console.log('📊 [Excel] Adding Success Criteria sheet with data:', {
    hasData: !!successCriteriaStats,
    isArray: Array.isArray(successCriteriaStats),
    length: successCriteriaStats?.length,
    firstItem: successCriteriaStats?.[0]
  });

  const csvData = transformSuccessCriteriaToCSV(successCriteriaStats);

  console.log('📊 [Excel] Success Criteria CSV data:', {
    totalRows: csvData.length,
    headers: csvData[0],
    sampleRows: csvData.slice(1, 4)
  });

  worksheet.mergeCells('A1:E1');
  worksheet.getCell('A1').value = 'Success Criteria Analysis';
  worksheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF' + COLORS.purple } };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.getRow(3).values = csvData[0];
  worksheet.getRow(3).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(3).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF' + COLORS.purple },
  };

  csvData.slice(1).forEach((row, idx) => {
    const rowNum = idx + 4;
    const excelRow = worksheet.getRow(rowNum);
    excelRow.values = row;

    const satisfiedCell = worksheet.getCell(rowNum, 3);
    const partialCell = worksheet.getCell(rowNum, 4);
    const notSatisfiedCell = worksheet.getCell(rowNum, 5);

    satisfiedCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFCEF6D8' }, // Light green
    };

    partialCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFEF3C7' }, // Light yellow
    };

    notSatisfiedCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFECACA' }, // Light red
    };
  });

  worksheet.getColumn(1).width = 30; // Criteria
  worksheet.getColumn(2).width = 40; // Objective
  worksheet.getColumn(3).width = 15; // Satisfied
  worksheet.getColumn(4).width = 20; // Partially Satisfied
  worksheet.getColumn(5).width = 15; // Not Satisfied

  const chartStartRow = csvData.length + 5;

  try {
    const stackedBarCanvas = createSuccessCriteriaStackedBarCanvas(csvData);

    if (stackedBarCanvas) {
      console.log('✅ [Excel] Generated success criteria stacked bar chart with', csvData.length - 1, 'items');
      const base64Image = canvasToBase64(stackedBarCanvas);

      const imageId = workbook.addImage({
        base64: base64Image,
        extension: 'png',
      });

      worksheet.mergeCells(`A${chartStartRow}:E${chartStartRow}`);
      const chartTitleCell = worksheet.getCell(`A${chartStartRow}`);
      chartTitleCell.value = 'Stacked Bar Chart: Success Criteria Distribution';
      chartTitleCell.font = { bold: true, size: 12, color: { argb: 'FF' + COLORS.purple } };
      chartTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      chartTitleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' },
      };

      const rowCount = csvData.length - 1;
      const calculatedHeight = 300 + rowCount * 95;

      worksheet.addImage(imageId, {
        tl: { col: 0, row: chartStartRow + 1 },
        ext: { width: 2600, height: calculatedHeight },
        editAs: 'oneCell'
      });

      console.log('✅ [Excel] Success Criteria chart added with', rowCount, 'bars, height:', calculatedHeight);
    } else {
      console.warn('⚠️ [Excel] Could not generate success criteria chart');
    }
  } catch (error) {
    console.error('❌ [Excel] Could not add success criteria chart:', error);
  }
}

async function addSummarySheet(worksheet, workbook, submissionsStats) {
  const csvData = transformSummaryToCSV(submissionsStats);

  worksheet.mergeCells('A1:B1');
  worksheet.getCell('A1').value = 'Class Insights Summary';
  worksheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF' + COLORS.purple } };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.getRow(3).values = csvData[0];
  worksheet.getRow(3).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(3).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF' + COLORS.purple },
  };

  csvData.slice(1).forEach((row, idx) => {
    const excelRow = worksheet.getRow(idx + 4);
    excelRow.values = row;

    const metricCell = worksheet.getCell(idx + 4, 1);
    const valueCell = worksheet.getCell(idx + 4, 2);

    metricCell.font = { bold: true };
    valueCell.font = { size: 11, bold: true };

    if (idx === 0) { // Total Submissions
      valueCell.font = { ...valueCell.font, color: { argb: 'FF' + COLORS.purple } };
      valueCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE9D5FF' }, // Light purple background
      };
    } else if (idx === 1) { // Classwork
      valueCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDD6FE' }, // Purple tint
      };
      valueCell.font = { ...valueCell.font, color: { argb: 'FF' + COLORS.purple } };
    } else if (idx === 2) { // Independent Study
      valueCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDCFCE7' }, // Green tint
      };
      valueCell.font = { ...valueCell.font, color: { argb: 'FF16A34A' } };
    } else if (idx === 3) { // Task Completion Rate
      const percentage = parseFloat(row[1]);
      if (percentage >= 80) {
        valueCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCEF6D8' }, // Light green
        };
      } else if (percentage >= 60) {
        valueCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFEF3C7' }, // Light yellow
        };
      } else {
        valueCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFECACA' }, // Light red
        };
      }
      valueCell.font = { ...valueCell.font, color: { argb: 'FF000000' } };
    } else if (idx === 4) { // On Time Rate
      const percentage = parseFloat(row[1]);
      if (percentage >= 80) {
        valueCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCEF6D8' }, // Light green
        };
      } else if (percentage >= 60) {
        valueCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFEF3C7' }, // Light yellow
        };
      } else {
        valueCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFECACA' }, // Light red
        };
      }
      valueCell.font = { ...valueCell.font, color: { argb: 'FF000000' } };
    } else if (idx === 5) { // Time Saved
      valueCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFEF9C3' }, // Light yellow background
      };
      valueCell.font = { ...valueCell.font, color: { argb: 'FF' + COLORS.yellow } };
    }
  });

  worksheet.getColumn(1).width = 30; // Metric
  worksheet.getColumn(2).width = 20; // Value

  const chartStartRow = csvData.length + 5;

  try {
    const donutCanvas = createSummaryDonutCanvas(submissionsStats);
    if (donutCanvas) {
      const base64Image = canvasToBase64(donutCanvas);
      const imageId = workbook.addImage({
        base64: base64Image,
        extension: 'png',
      });

      worksheet.mergeCells(`A${chartStartRow}:B${chartStartRow}`);
      const chartTitleCell = worksheet.getCell(`A${chartStartRow}`);
      chartTitleCell.value = 'Visual Chart: Submission Type Distribution';
      chartTitleCell.font = { bold: true, size: 12, color: { argb: 'FF' + COLORS.purple } };
      chartTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      chartTitleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' },
      };

      worksheet.addImage(imageId, {
        tl: { col: 0, row: chartStartRow + 1 },
        ext: { width: 640, height: 380 },
        editAs: 'oneCell',
      });
    }
  } catch (error) {
    console.warn('📊 [Excel] Could not add summary chart:', error);
  }
}

async function addRubricsSheet(worksheet, workbook, rubricsStats) {
  const csvData = transformRubricsToCSV(rubricsStats);

  worksheet.mergeCells('A1:E1');
  worksheet.getCell('A1').value = 'Rubrics Analysis';
  worksheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF' + COLORS.purple } };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.getRow(3).values = csvData[0];
  worksheet.getRow(3).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(3).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF' + COLORS.purple },
  };

  csvData.slice(1).forEach((row, idx) => {
    const excelRow = worksheet.getRow(idx + 4);
    excelRow.values = row;

    // Enable text wrapping for criteria cells
    excelRow.eachCell((cell, colNumber) => {
      const header = csvData[0][colNumber - 1];
      const isFixedColumn = colNumber <= 4; // Rank, Student, Question, Task Name

      cell.alignment = {
        vertical: 'top',
        horizontal: isFixedColumn ? 'left' : 'center',
        wrapText: !isFixedColumn
      };
    });
  });

  const headers = csvData[0];
  headers.forEach((header, index) => {
    const columnIndex = index + 1;
    const headerStr = String(header || '');

    if (headerStr === 'Rank' || headerStr.includes('Rank')) {
      worksheet.getColumn(columnIndex).width = 8;
    } else if (headerStr === 'Student' || headerStr.includes('Student Name')) {
      worksheet.getColumn(columnIndex).width = 25;
    } else if (headerStr.includes('Task Name')) {
      worksheet.getColumn(columnIndex).width = 50;
    } else if (headerStr.includes('Question')) {
      worksheet.getColumn(columnIndex).width = 12;
    } else if (headerStr.includes('Grade Level')) {
      worksheet.getColumn(columnIndex).width = 30;
    } else {
      // Criteria columns - use header length to determine width
      // The criteria name is now in the header, value is just the grade
      const calculatedWidth = Math.min(Math.max(headerStr.length * 1.2, 20), 50);
      worksheet.getColumn(columnIndex).width = calculatedWidth;
    }
  });

  // No chart for rubrics - only data table
  console.log('📊 [Excel] Rubrics sheet: data table only, no chart');
}

async function addShortAnswerSheet(worksheet, shortAnswerStats) {
  const csvData = transformShortAnswerToCSV(shortAnswerStats);

  worksheet.mergeCells('A1:G1');
  worksheet.getCell('A1').value = 'Short Answer Analysis';
  worksheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF' + COLORS.purple } };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.getRow(3).values = csvData[0];
  worksheet.getRow(3).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(3).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF' + COLORS.purple },
  };

  csvData.slice(1).forEach((row, idx) => {
    const excelRow = worksheet.getRow(idx + 4);
    excelRow.values = row;

    const headers = csvData[0];
    const numeratorIndex = headers.findIndex(h => h === 'Score Numerator');
    const denominatorIndex = headers.findIndex(h => h === 'Score Denominator');
    const avgIndex = headers.findIndex(h => h === 'Student Average %' || h === 'Average');

    if (numeratorIndex >= 0 && denominatorIndex >= 0) {
      const numerator = Number(row[numeratorIndex]);
      const denominator = Number(row[denominatorIndex]);
      const avg = avgIndex >= 0 ? Number(String(row[avgIndex] || '').replace('%', '')) : 0;
      const percentFromScore = denominator > 0 ? (numerator / denominator) * 100 : avg;
      const gradientColor = interpolateGradientColor(percentFromScore);

      [numeratorIndex + 1, denominatorIndex + 1, avgIndex >= 0 ? avgIndex + 1 : null].filter(Boolean).forEach((columnIndex) => {
        const cell = worksheet.getCell(idx + 4, columnIndex);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: gradientColor },
        };
        cell.font = { color: { argb: 'FF000000' }, bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
    }
  });

  const headers = csvData[0];
  headers.forEach((header, index) => {
    const columnIndex = index + 1;
    const headerStr = String(header || '');

    if (headerStr === 'Rank' || headerStr.includes('Rank')) {
      worksheet.getColumn(columnIndex).width = 8;
    } else if (headerStr === 'Student' || headerStr.includes('Student Name')) {
      worksheet.getColumn(columnIndex).width = 25;
    } else if (headerStr.includes('Task Name')) {
      worksheet.getColumn(columnIndex).width = 35;
    } else if (headerStr === 'Question') {
      worksheet.getColumn(columnIndex).width = 30;
    } else if (headerStr.includes('Score') || headerStr.includes('Numerator') || headerStr.includes('Denominator')) {
      worksheet.getColumn(columnIndex).width = 15;
    } else if (headerStr.includes('Average')) {
      worksheet.getColumn(columnIndex).width = 15;
    } else {
      worksheet.getColumn(columnIndex).width = Math.max(12, Math.min(headerStr.length + 3, 25));
    }
  });
}

export async function exportCommentBanksToExcel(commentBankStats, filename) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Comment Banks');

  await addCommentBanksSheet(worksheet, workbook, commentBankStats);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
}

export async function exportSuccessCriteriaToExcel(successCriteriaStats, filename) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Success Criteria');

  await addSuccessCriteriaSheet(worksheet, workbook, successCriteriaStats);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
}

export async function exportSummaryToExcel(submissionsStats, filename) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Summary');

  await addSummarySheet(worksheet, workbook, submissionsStats);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
}

export async function exportRubricsToExcel(rubricsStats, filename) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Rubrics');

  await addRubricsSheet(worksheet, workbook, rubricsStats);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
}

export async function exportShortAnswerToExcel(shortAnswerStats, filename) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Short Answer');

  await addShortAnswerSheet(worksheet, shortAnswerStats);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
}

export async function exportFullReportToExcel(allData, filename) {
  // Helper function to check if data has actual content
  const hasCommentBankData = Array.isArray(allData.commentBankStats) && allData.commentBankStats.length > 0;
  const hasSuccessCriteriaData = Array.isArray(allData.successCriteriaStats) && allData.successCriteriaStats.length > 0;
  const hasRubricsData = Array.isArray(allData.rubricsStats) && allData.rubricsStats.length > 0;
  const hasShortAnswerData = allData.shortAnswerStats &&
    ((Array.isArray(allData.shortAnswerStats.tasks) && allData.shortAnswerStats.tasks.length > 0) ||
     (Array.isArray(allData.shortAnswerStats) && allData.shortAnswerStats.length > 0));

  console.log('📊 [Excel] exportFullReportToExcel starting (data-only, no screenshots):', {
    filename,
    hasSubmissions: !!allData.submissionsStats,
    commentBanks: allData.commentBankStats?.length ?? 0,
    successCriteria: allData.successCriteriaStats?.length ?? 0,
    rubrics: allData.rubricsStats?.length ?? 0,
    shortAnswer: allData.shortAnswerStats?.tasks?.length ?? allData.shortAnswerStats?.length ?? 0,
    willIncludeCommentBanks: hasCommentBankData,
    willIncludeSuccessCriteria: hasSuccessCriteriaData,
    willIncludeRubrics: hasRubricsData,
    willIncludeShortAnswer: hasShortAnswerData,
  });

  const workbook = new ExcelJS.Workbook();

  // Always include Summary sheet if submissions data exists
  if (allData.submissionsStats) {
    const summarySheet = workbook.addWorksheet('Summary');
    await addSummarySheet(summarySheet, workbook, allData.submissionsStats);
  }

  // Only include sheets with actual data
  if (hasCommentBankData) {
    console.log('📊 [Excel] Adding Comment Banks sheet with', allData.commentBankStats.length, 'banks');
    const commentBanksSheet = workbook.addWorksheet('Comment Banks');
    await addCommentBanksSheet(commentBanksSheet, workbook, allData.commentBankStats);
  } else {
    console.log('⏭️ [Excel] Skipping Comment Banks sheet - no data available');
  }

  if (hasSuccessCriteriaData) {
    console.log('📊 [Excel] Adding Success Criteria sheet with', allData.successCriteriaStats.length, 'criteria');
    const successCriteriaSheet = workbook.addWorksheet('Success Criteria');
    await addSuccessCriteriaSheet(successCriteriaSheet, workbook, allData.successCriteriaStats);
  } else {
    console.log('⏭️ [Excel] Skipping Success Criteria sheet - no data available');
  }

  if (hasRubricsData) {
    console.log('📊 [Excel] Adding Rubrics sheet with', allData.rubricsStats.length, 'rubric tasks');
    const rubricsSheet = workbook.addWorksheet('Rubrics');
    await addRubricsSheet(rubricsSheet, workbook, allData.rubricsStats);
  } else {
    console.log('⏭️ [Excel] Skipping Rubrics sheet - no data available');
  }

  if (hasShortAnswerData) {
    const taskCount = allData.shortAnswerStats.tasks?.length ?? allData.shortAnswerStats.length ?? 0;
    console.log('📊 [Excel] Adding Short Answer sheet with', taskCount, 'tasks');
    const shortAnswerSheet = workbook.addWorksheet('Short Answer');
    await addShortAnswerSheet(shortAnswerSheet, allData.shortAnswerStats);
  } else {
    console.log('⏭️ [Excel] Skipping Short Answer sheet - no data available');
  }

  const sheetNames = workbook.worksheets.map((ws) => ws.name);
  console.log('✅ [Excel] exportFullReportToExcel complete:', { filename, sheets: sheetNames });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
}

function downloadExcel(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function exportSectionToExcel(section, data, filename) {
  switch (section) {
    case SECTIONS.COMMENT_BANKS:
      return exportCommentBanksToExcel(data, filename);
    case SECTIONS.SUCCESS_CRITERIA:
      return exportSuccessCriteriaToExcel(data, filename);
    case SECTIONS.SUMMARY:
      return exportSummaryToExcel(data, filename);
    case SECTIONS.RUBRICS:
      return exportRubricsToExcel(data, filename);
    case SECTIONS.SHORT_ANSWER:
      return exportShortAnswerToExcel(data, filename);
    default:
      throw new Error(`Unknown section type: ${section}`);
  }
}
